import React, { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';
import clsx from 'clsx';
import axios from "axios";
import { useLocation } from "react-router-dom";

import { lighten } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import LinearProgress from "@mui/material/LinearProgress";
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import ModelOrdersNewDialog from "./ModelOrdersNewDialog";
import {axiosDelay} from "../../../config/config";

import {
    appSetAlert, appSetSettingsContent
} from "../../../redux/app/appActions";

const PREFIX = 'UserGroupLayout';

const classes = {
	root: `${PREFIX}-root`,
	paper: `${PREFIX}-paper`,
	linearProgress: `${PREFIX}-linearProgress`,
	table: `${PREFIX}-table`,
	tableContainer: `${PREFIX}-tableContainer`,
	visuallyHidden: `${PREFIX}-visuallyHidden`
};

const Root = styled('div')((
	{
		theme
	}
) => ({
	[`&.${classes.root}`]: {
		width: '100%'
	},

	[`& .${classes.paper}`]: {
		height: '100%',
		width: 'calc(100% - 16px)',
		margin: '8px'
	},

	[`& .${classes.linearProgress}`]: {
		height: '1px'
	},

	[`& .${classes.table}`]: {
		minWidth: '450px'
	},

	[`& .${classes.tableContainer}`]: {
		height: 'calc(100% - 120px)',
		minHeight: '350px',
		borderRadius: 0,
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
	},

	[`& .${classes.visuallyHidden}`]: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1,
	}
}));

function createData(modelOrder, modelOrdersID) {
    return { modelOrder, modelOrdersID };
}

let rows = [
    // Sample
    // createData('tyler.huehmer@jacobs.com', 'SU', 'Super User', 'ObjectID'),
];

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

const headCells = [
	{ id: 'modelorderid', numeric: false, disablePadding: false, label: 'Row ID' },
	{ id: 'modelorder', numeric: false, disablePadding: false, label: 'Order' }
];

function EnhancedTableHead(props) {
    const {  onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                {/*<TableCell padding="checkbox">*/}
                {/*    <div></div>*/}
                {/*</TableCell>*/}
                {/*<TableCell padding="checkbox">*/}
                {/*    <Checkbox*/}
                {/*        indeterminate={numSelected > 0 && numSelected < rowCount}*/}
                {/*        checked={rowCount > 0 && numSelected === rowCount}*/}
                {/*        onChange={onSelectAllClick}*/}
                {/*        inputProps={{ 'aria-label': 'select all desserts' }}*/}
                {/*    />*/}
                {/*</TableCell>*/}
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <span className={classes.visuallyHidden}>
								  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</span>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
                <TableCell padding="checkbox">
                    <Root></Root>
                </TableCell>
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};


const PREFIX2 = 'EnhancedTableToolbar';

const classes2 = {
	root: `${PREFIX2}-root`,
	highlight: `${PREFIX2}-highlight`,
	title: `${PREFIX2}-title`
};

const StyledEnhancedTableToolbar = styled(Toolbar)((
	{
		theme
	}
) => ({
	[`&.${classes2.root}`]: {
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(1),
	},

	[`& .${classes.highlight}`]: theme.palette.mode === 'light'
		? {
			color: theme.palette.secondary.main,
			backgroundColor: lighten(theme.palette.secondary.light, 0.85),
		}
		: {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.secondary.dark,
		},

	[`& .${classes2.title}`]: {
		flex: '1 1 100%',
	}
}));

const EnhancedTableToolbar = props => {
    const { numSelected, configuration, modelID, handleReloadRecords } = props;

    const [newDialogVisible, setNewDialogVisible] = React.useState(false);

    const showNewDialog = row => {
        setNewDialogVisible(true);
    };

    const hideNewDialog = reload => {
        setNewDialogVisible(false);

        // Reload
        if (reload) {
            handleReloadRecords();
        }
    };

    return (
        <React.Fragment>
            {newDialogVisible &&
                <ModelOrdersNewDialog configuration={configuration} modelID={modelID} handleClose={hideNewDialog}/>
            }
            <StyledEnhancedTableToolbar
                className={clsx(classes2.root, {
                    [classes.highlight]: numSelected > 0,
                })}
            >
                {numSelected > 0 ? (
                    <Typography className={classes2.title} color="inherit" variant="subtitle1">
                        {numSelected} selected
                    </Typography>
                ) : (
                    <Typography className={classes2.title} variant="subtitle1" id="tableTitle">
                        Add Model Version Orders
                    </Typography>
                )}
                <Tooltip title="New User">
                    <IconButton aria-label="New User" onClick={showNewDialog} size="large">
                        <AddIcon/>
                    </IconButton>
                </Tooltip>
                {/*{numSelected > 0 ? (*/}
                {/*    <Tooltip title="Delete">*/}
                {/*        <IconButton aria-label="delete" size="large">*/}
                {/*            <DeleteIcon />*/}
                {/*        </IconButton>*/}
                {/*    </Tooltip>*/}
                {/*) : (*/}
                {/*    <React.Fragment>*/}
                {/*    /!*<Tooltip title="Filter list">*!/*/}
                {/*    /!*    <IconButton aria-label="filter list">*!/*/}
                {/*    /!*        <FilterListIcon />*!/*/}
                {/*    /!*    </IconButton>*!/*/}
                {/*    /!*</Tooltip>*!/*/}
                {/*    </React.Fragment>*/}
                {/*)}*/}
            </StyledEnhancedTableToolbar>
        </React.Fragment>
    );
};

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
	configuration: PropTypes.object.isRequired,
	modelID: PropTypes.number.isRequired,
    handleReloadRecords: PropTypes.func.isRequired
};

export default function EnhancedTable({xId, configuration}) {
    const dispatch = useDispatch();

    // Component State
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('calories');
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [editRowState, setEditRowState] = React.useState(0);
    const [dataLoaded, setDataLoaded] = React.useState(false);

    // App State
    const config = useSelector(state => state.config);
	const modalContent = useSelector(state => state.app.modalContent);

    // Get active location of app
    const location = useLocation();

    // Load up Model Versions Orders
    useEffect(() => {

        setDataLoaded(false);
        rows = [];

        setTimeout(function () {
            const apiUrl = "{0}DBQuery?table=ModelOrders&where=ModelID eq {1}&db={2}".format(config.data.authentication.api, modalContent.filterValue, config.data.authentication.apiSource);
            axios
                .get('{0}'.format(apiUrl))
                .then(function (response) {

                    for (const data of response.data) {
                        rows.push(createData(data.modelOrder, data.modelOrdersID));
                    }

					setDataLoaded(true);
                })
                .catch(function (error) {
                    console.log('**ERROR**', error);

                    dispatch(appSetAlert('Whoops!', 'Something went seriously wrong. Please try again.', 'error', 10000));

					setDataLoaded(true);
                });

        }, JSON.parse(axiosDelay()));
    }, [editRowState]);

    // Settings Drawer
    useEffect(() => {
        dispatch(appSetSettingsContent({ icon: null, content: {location: location.pathname, content: null} }));
    }, []);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = event => {
        if (event.target.checked) {
            const newSelecteds = rows.map(n => n.email);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const reloadRecords = () => {
        const newStateToReload = editRowState + 1;
        setEditRowState(newStateToReload);
    };

    const isSelected = name => selected.indexOf(name) !== -1;

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

    return (
        <Root className={classes.root}>
			<Paper className={classes.paper}>
				{configuration && modalContent.filterValue &&
					<EnhancedTableToolbar numSelected={selected.length}
										  configuration={configuration}
										  modelID={modalContent.filterValue}
										  handleReloadRecords={reloadRecords}/>
				}
				{!dataLoaded &&
					<LinearProgress className={classes.linearProgress} />
				}
				<TableContainer className={classes.tableContainer}>
					<Table
						className={classes.table}
						aria-labelledby="tableTitle"
						size="medium"
						aria-label="enhanced table"
					>
						<EnhancedTableHead
							classes={classes}
							numSelected={selected.length}
							order={order}
							orderBy={orderBy}
							onSelectAllClick={handleSelectAllClick}
							onRequestSort={handleRequestSort}
							rowCount={rows.length}
						/>
						<TableBody>
							<React.Fragment>
								{stableSort(rows, getComparator(order, orderBy))
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((row, index) => {
										const isItemSelected = isSelected(row.modelOrdersID);
										const labelId = `enhanced-table-checkbox-${index}`;  // https://eslint.org/docs/rules/no-template-curly-in-string

										return (
											<TableRow
												hover
												role="checkbox"
												aria-checked={isItemSelected}
												tabIndex={-1}
												key={row.modelOrdersID}
												selected={isItemSelected}
											>
												<TableCell component="th" id={labelId} scope="row">{row.modelOrdersID}</TableCell>
												<TableCell align="left">{row.modelOrder}</TableCell>
												{/*<TableCell padding="checkbox" style={{width: '120px', textAlign: 'center'}}>*/}
												{/*	<ConfirmationButton type="delete" tooltip="Delete" handleConfirm={() => {deleteUser(row)}}/>*/}
												{/*</TableCell>*/}
											</TableRow>
										);
									})}
								{emptyRows > 0 && (
									<TableRow style={{height: 53 * emptyRows}}>
										<TableCell colSpan={6}/>
									</TableRow>
								)}
							</React.Fragment>
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[5, 10, 15]}
					component="div"
					count={rows.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={handleChangePage}
					onRowsPerPageChange={handleChangeRowsPerPage}
				/>
			</Paper>
		</Root>
    );
}